import React, { useState, useEffect, useRef } from 'react';
import { AutoComplete, Form, Input, Select, InputNumber, Button, Card, Row, Col, Divider, message, Steps, Tag, Radio, DatePicker, Typography, Alert, Space, Table, Descriptions, Switch } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, CalculatorOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../services/api';
import useAvisoAlteracoes from '../hooks/useAvisoAlteracoes';
import UploadFotos from '../components/UploadFotos';
import ManometroForm from '../components/ManometroForm';
import ValvulaForm from '../components/ValvulaForm';
import VidaRemanescente from '../components/VidaRemanescente';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const TIPOS_LAUDO = [
  'Laudo de Inspeção Interna NR-13','Laudo de Inspeção Externa NR-13','Laudo de Inspeção Interna e Externa NR-13',
  'Laudo de Calibração de Manômetro','Laudo de Calibração de Válvula de Segurança',
  'Laudo de Calibração de Termômetro','Laudo de Calibração de Calibrador de Pneu',
  'Laudo de Calibração de Pressostato','Laudo de Calibração de Incubadora',
  'Laudo de Manutenção Preventiva de Autoclave','Laudo de Ensaio por Líquido Penetrante',
  'Laudo de Inspeção de Tubulação','Laudo de Ensaio por Corrente Parasita',
  'Laudo de Estanqueidade de Tubulação','Laudo de Estanqueidade de Tanque',
];

const CHECKLIST_ITENS = [
  { key: 'cl_bases_assentamento', label: 'Inspeção das bases de assentamento do vaso' },
  { key: 'cl_pintura', label: 'Inspeção da pintura' },
  { key: 'cl_bocais_soldados', label: 'Inspeção dos bocais soldados ao recipiente' },
  { key: 'cl_aterramento', label: 'Verificação do aterramento do vaso' },
  { key: 'cl_fixacao_base', label: 'Inspeção da fixação da base' },
  { key: 'cl_inspecao_solda', label: 'Inspeção Visual da solda' },
  { key: 'cl_chapas', label: 'Inspeção das chapas' },
  { key: 'cl_boca_visita', label: 'Inspeção da boca de visita/bocal do recipiente' },
  { key: 'cl_aneis_juntas', label: 'Inspeção dos anéis e juntas de vedação dos bocais' },
  { key: 'cl_iluminacao_emergencia', label: 'Iluminação de emergência (OBRIGATÓRIO)' },
  { key: 'cl_extintor', label: 'Extintor (obrigatório para fluidos combustíveis e inflamáveis)' },
  { key: 'cl_teste_hidrostatico', label: 'Realização do teste hidrostático' },
  { key: 'cl_valvulas_manometro', label: 'Verificação das válvulas de segurança e manômetro' },
  { key: 'cl_pressostatos', label: 'Verificação de pressostatos' },
  { key: 'cl_medidor_nivel', label: 'Verificação do Medidor de Nível' },
  { key: 'cl_termometro', label: 'Verificação do Termômetro' },
  { key: 'cl_dreno', label: 'Verificação do Dreno' },
  { key: 'cl_placa_identificacao', label: 'Placa de identificação' },
  { key: 'cl_acesso_manutencao', label: 'Dispor de acesso fácil e seguro para as atividades de manutenção, operação e inspeção.' },
  { key: 'cl_ventilacao_permanente', label: 'Dispor de ventilação permanente com entradas de ar que não possam ser bloqueadas.' },
  { key: 'cl_iluminacao_normas', label: 'Dispor de iluminação conforme normas oficiais vigentes.' },
];

const VS_CHECKLIST = [
  { key: 'vs_cl_pintura', label: 'Pintura' },
  { key: 'vs_cl_placa', label: 'Placa de Identificação' },
  { key: 'vs_cl_obturador', label: 'Condições do Obturador e Disco' },
  { key: 'vs_cl_alavanca', label: 'Alavanca de Acionamento' },
  { key: 'vs_cl_haste_mola', label: 'Haste/Mola' },
  { key: 'vs_cl_corpo', label: 'Condições do Corpo, Castelo e Capuz' },
  { key: 'vs_cl_anel', label: 'Anel de Regulagem' },
  { key: 'vs_cl_entupimento', label: 'Entupimento por Condições Externa' },
  { key: 'vs_cl_conexoes', label: 'Condições de suas Conexões (Rosca/Flange)' },
];

// Recomendacoes: refaz as linhas automaticas e preserva o texto escrito a mao
const mesclarObs = (atual, novas) => {
  const txt = v => Array.isArray(v) ? v.join('\n') : String(v || '');
  const auto = /^(\p{Extended_Pictographic}|•|Motivo:)/u;
  const manuais = txt(atual).split('\n').filter(l => l.trim() && !auto.test(l.trim()));
  return [txt(novas).trim(), manuais.join('\n').trim()].filter(Boolean).join('\n\n');
};

const STEPS = ['Identificação','Equipamento','Dimensional','Vida Remanescente','Check List','Manômetro','Válvula','Resultado','Revisão'];

function LaudoForm() {
  const { id } = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const osIdFromUrl = urlParams.get('os_id');
  const tipoFromUrl = urlParams.get('tipo');
  const subtipoFromUrl = urlParams.get('subtipo');
  const subsubtipoFromUrl = urlParams.get('subsubtipo');
  const clienteIdFromUrl = urlParams.get('cliente_id');
  const propostaIdFromUrl = urlParams.get('proposta_id');
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Croqui da etapa Dimensional. O desenho vem do backend, do mesmo gerador
  // que o PDF usa - desenhar aqui de novo seria criar uma segunda verdade.
  const [croquiSvg, setCroquiSvg] = useState('');
  const [croquiMsg, setCroquiMsg] = useState('');
  const desenharCroqui = async () => {
    const v = form.getFieldsValue(true);
    const temD = parseFloat(v.diametro_externo) > 0 || parseFloat(v.medida_circunferencial) > 0;
    if (!temD || !(parseFloat(v.distancia_solda_solda) > 0)) {
      setCroquiSvg('');
      setCroquiMsg('Preencha o diâmetro externo (ou a medida circunferencial) e a distância solda a solda.');
      return;
    }
    try {
      const r = await api.post('/laudos/croqui/previa', v);
      setCroquiSvg(r.data.svg || '');
      setCroquiMsg(r.data.svg ? '' : 'Não foi possível desenhar com estas cotas.');
    } catch (e) {
      setCroquiSvg('');
      setCroquiMsg('Erro ao gerar o croqui.');
    }
  };
  useEffect(() => { if (step === 2) desenharCroqui(); // eslint-disable-next-line
  }, [step]);
  const [croquis, setCroquis] = useState({});
  const [croquiSelecionado, setCroquiSelecionado] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [cnpjs, setCnpjs] = useState([]);
  const [todosCnpjs, setTodosCnpjs] = useState([]);
  const [nomeClienteLaudo, setNomeClienteLaudo] = useState("");
  const [veioDaOS, setVeioDaOS] = useState(false);
  const [materiaisTensao, setMateriaisTensao] = useState([]);
  const [artFilial, setArtFilial] = useState('');
  const [calculo, setCalculo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [possuiManometro, setPossuiManometro] = useState(false);
  const [thCalculado, setThCalculado] = useState('');
  const [fluidosNR13, setFluidosNR13] = useState([]);
  const [possuiValvula, setPossuiValvula] = useState(false);
  const dadosRef = useRef({});
  const [form] = Form.useForm();
  useAvisoAlteracoes(() => form.isFieldsTouched());
  const isEdicao = !!id;
  const laudoIdRef = useRef(id || null);
  const criandoRef = useRef(false);
  useEffect(() => { if (id) laudoIdRef.current = id; }, [id]);

  useEffect(() => {
    carregarClientes();
    api.get('/materiais').then(r => setMateriaisTensao(r.data || [])).catch(() => {});
    api.get('/clientes/cnpjs/todos').then(r => setTodosCnpjs(r.data || [])).catch(() => {});
    api.get('/classificacao-nr13/fluidos').then(r => setFluidosNR13(r.data || [])).catch(() => {});
    // Carregar croquis disponíveis
    const tiposCroqui = ['croqui_vertical','croqui_horizontal','croqui_esferico','croqui_cilindrico','croqui_torisf','croqui_conico','croqui_me'];
    const labelsCroqui = { croqui_vertical: 'Vaso Vertical', croqui_horizontal: 'Vaso Horizontal', croqui_esferico: 'Vaso Esférico', croqui_cilindrico: 'Vaso Cilíndrico', croqui_torisf: 'Tampo Torisférico', croqui_conico: 'Tampo Cônico', croqui_me: 'Medição de Espessura' };
    Promise.all(tiposCroqui.map(t => api.get('/upload/padrao/' + t + '/status').then(r => ({ tipo: t, label: labelsCroqui[t], ...r.data })).catch(() => ({ tipo: t, label: labelsCroqui[t], existe: false }))))
      .then(results => { const obj = {}; results.filter(r => r.existe).forEach(r => { obj[r.tipo] = r; }); setCroquis(obj); });
    // Preencher tecnico com usuario logado se novo laudo
    if (!id) {
      try {
        const token = localStorage.getItem('token');
        const payload = JSON.parse(atob(token.split('.')[1]));
        form.setFieldsValue({ tecnico: payload.nome });
      } catch {}
    }
    if (id) {
      carregarLaudo();
    } else if (osIdFromUrl) {
      // Pré-preencher a partir da OS
      carregarDadosOS();
    }
  }, [id, osIdFromUrl]);

  const carregarDadosOS = async () => {
    try {
      const r = await api.get('/os/' + osIdFromUrl);
      const os = r.data;
      // Pré-preencher cliente e OS
      if (os.cliente_id) {
        await selecionarCliente(os.cliente_id);
      }
      // Usar cnpj_faturamento_id direto da OS
      const cnpjFaturamentoId = (os.cnpj_laudo_id || os.cnpj_faturamento_id);
      if (cnpjFaturamentoId) {
        form.setFieldsValue({ cnpj_laudo_id: cnpjFaturamentoId });
        try {
          const allCnpjs = await api.get('/clientes/cnpjs/todos');
          const cnpjSel = (allCnpjs.data || []).find(cn => cn.id === cnpjFaturamentoId);
          if (cnpjSel?.art_numero) setArtFilial(cnpjSel.art_numero);
        } catch {}
      } else if (os.proposta_id) {
        try {
          const rp = await api.get('/propostas/' + os.proposta_id);
          const cnpjId = rp.data.cnpj_faturamento_id;
          if (cnpjId) {
            form.setFieldsValue({ cnpj_laudo_id: cnpjId });
            const allCnpjs = await api.get('/clientes/cnpjs/todos');
            const cnpjSel = (allCnpjs.data || []).find(cn => cn.id === cnpjId);
            if (cnpjSel?.art_numero) setArtFilial(cnpjSel.art_numero);
          }
        } catch {}
      }
      // Montar tipo do laudo
      // "Interna e Externa", "Criogenia - Interna", etc — o que vale e o fim
      const fimDoSub = String(subsubtipoFromUrl || '').split(' - ').pop().trim();
      const modoDoExame = fimDoSub === 'Interna' ? 'interna'
        : fimDoSub === 'Interna e Externa' ? 'interna_externa' : null;

      let tipoLaudo = tipoFromUrl || '';
      if (tipoFromUrl === 'inspecao') {
        // padrao unico: Inspeção NR13 - Interna / Externa / Interna e Externa
        const ROTULO_MODO = { interna: 'Interna', externa: 'Externa', interna_externa: 'Interna e Externa' };
        tipoLaudo = 'Inspeção NR13 - ' + (ROTULO_MODO[modoDoExame] || 'Interna e Externa');
      } else if (tipoFromUrl === 'calibracao') {
        tipoLaudo = 'Calibração — ' + (subtipoFromUrl || '');
      } else if (tipoFromUrl === 'estanqueidade') {
        tipoLaudo = 'Estanqueidade — ' + (subtipoFromUrl || '');
      } else if (tipoFromUrl === 'end') {
        tipoLaudo = 'END — Líquido Penetrante';
      } else if (tipoFromUrl === 'manutencao') {
        tipoLaudo = 'Manutenção — Autoclave';
      } else if (tipoFromUrl === 'diversos') {
        tipoLaudo = 'Diversos';
      }
      setNomeClienteLaudo((os.cnpj_filial || '') + (os.razao_filial ? ' — ' + os.razao_filial : ''));
      setVeioDaOS(true);
      form.setFieldsValue({
        os_numero: os.numero,
        numero_os: os.numero,
        os_id: os.id,
        cliente_id: os.cliente_id || null,
        cnpj_laudo_id: (os.cnpj_laudo_id || os.cnpj_faturamento_id) || null,
        tecnico: os.tecnico || form.getFieldValue('tecnico'),
        tipo_laudo: tipoLaudo,
        tipo_inspecao_modo: modoDoExame || 'interna_externa',
        endereco_atendimento: os.endereco_atendimento || '',
        proposta_id: propostaIdFromUrl || os.proposta_id,
      });
    } catch(e) { message.error('Erro ao carregar dados da OS'); }
  };

  const carregarClientes = async () => {
    try { const r = await api.get('/clientes'); setClientes(r.data); }
    catch { message.error('Erro ao carregar clientes'); }
  };

  const carregarLaudo = async () => {
    try {
      setLoading(true);
      const r = await api.get(`/laudos/${id}`);
      const l = r.data;
      if (l.cliente_id) {
        const cnpjsData = await api.get(`/clientes/${l.cliente_id}/cnpjs`);
        setCnpjs(cnpjsData.data);
        if (l.cnpj_laudo_id) {
          const cnpjSel = (cnpjsData.data || []).find(cn => cn.id === l.cnpj_laudo_id);
          if (cnpjSel?.art_numero) setArtFilial(cnpjSel.art_numero);
        }
      }
      setPossuiManometro(l.possui_manometro || false);
      if (l.numero_os) {
        setVeioDaOS(true);
        // Buscar nome do cliente e endereço de atendimento da OS
        try {
          const osR = await api.get('/os/buscar?numero=' + encodeURIComponent(l.numero_os));
          const osData = osR.data;
          if (osData) {
            dadosRef.current = { ...dadosRef.current, tecnico: osData.tecnico, numero_os: osData.numero, endereco_atendimento: osData.endereco_atendimento };
            setTimeout(() => {
              form.setFieldsValue({
                endereco_atendimento: osData.endereco_atendimento || '',
                tecnico: osData.tecnico || '',
                numero_os: osData.numero || '',
              });
            }, 300);
            if (osData.cnpj_filial) setNomeClienteLaudo(osData.cnpj_filial + ' — ' + (osData.razao_filial || ''));
            else if (osData.razao_social) setNomeClienteLaudo(osData.razao_social);
          }
        } catch {}
        // Buscar nome do cliente da filial se não veio da OS
        if (!nomeClienteLaudo && l.cnpj_laudo_id) {
          try {
            const allCnpjs = await api.get('/clientes/cnpjs/todos');
            const cnpjSel = (allCnpjs.data || []).find(cn => cn.id === l.cnpj_laudo_id);
            if (cnpjSel) setNomeClienteLaudo(cnpjSel.cnpj + ' — ' + cnpjSel.razao_social);
          } catch {}
        }
      }
      setPossuiValvula(l.possui_valvula || false);
      const formData = {
        ...l,
        data_inspecao: l.data_inspecao ? dayjs(l.data_inspecao.substring(0,10)) : null,
        data_th: l.data_th ? dayjs(l.data_th.substring(0,10)) : null,
        proxima_inspecao_externa: l.proxima_inspecao_externa ? dayjs(l.proxima_inspecao_externa.substring(0,10)) : null,
        proxima_inspecao_interna: l.proxima_inspecao_interna ? dayjs(l.proxima_inspecao_interna.substring(0,10)) : null,
      };
      // Não sobrescrever tecnico e numero_os com null — virão da OS
      if (!formData.tecnico) delete formData.tecnico;
      if (!formData.numero_os) delete formData.numero_os;
      form.setFieldsValue(formData);
      // Salvar dados diretamente do servidor no ref
      dadosRef.current = {
        ...l,
        possui_manometro: l.possui_manometro || false,
        possui_valvula: l.possui_valvula || false,
      };
    } catch { message.error('Erro ao carregar laudo'); }
    finally { setLoading(false); }
  };

  const selecionarCliente = async (clienteId) => {
    try { const r = await api.get(`/clientes/${clienteId}/cnpjs`); setCnpjs(r.data); }
    catch {}
  };

  const salvarDados = () => {
    const vals = form.getFieldsValue(true);
    dadosRef.current = {
      ...dadosRef.current,
      ...vals,
      possui_manometro: possuiManometro,
      possui_valvula: possuiValvula,
    };
  };

  const irPara = async (novoStep) => {
    const vals = form.getFieldsValue(true);
    const clKeys = ['cl_bases_assentamento','cl_pintura','cl_bocais_soldados','cl_aterramento','cl_fixacao_base','cl_inspecao_solda','cl_chapas','cl_boca_visita','cl_aneis_juntas','cl_iluminacao_emergencia','cl_extintor','cl_teste_hidrostatico','cl_valvulas_manometro','cl_pressostatos','cl_medidor_nivel','cl_termometro','cl_dreno','cl_placa_identificacao','cl_acesso_manutencao','cl_ventilacao_permanente','cl_iluminacao_normas'];
    // Preservar checklist — só atualiza se o novo valor não for null
    const clPreservados = {};
    clKeys.forEach(k => { clPreservados[k] = (vals[k] != null && vals[k] !== '') ? vals[k] : (dadosRef.current[k] || null); });
    // Filtrar apenas valores nao nulos para nao sobrescrever dados existentes
    const valsLimpos = {};
    Object.entries(vals).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') valsLimpos[k] = v; });
    dadosRef.current = { ...dadosRef.current, ...valsLimpos, ...clPreservados, possui_manometro: possuiManometro, possui_valvula: possuiValvula };
    setStep(novoStep);
    // Recalcular recomendações ao chegar na etapa 7 (Resultado)
    if (novoStep === 7) {
      try {
        const r = await api.post('/laudos/calcular', dadosRef.current);
        if (r.data.recomendacoes) {
          const texto = Array.isArray(r.data.recomendacoes) ? r.data.recomendacoes.join('\n') : r.data.recomendacoes;
          if (!dadosRef.current.observacoes_manual && !form.getFieldValue('observacoes_manual')) { const m = mesclarObs(form.getFieldValue('observacoes') || dadosRef.current.observacoes, texto); form.setFieldsValue({ observacoes: m }); dadosRef.current.observacoes = m; }
        }
        if (r.data.resultado) {
          form.setFieldsValue({ resultado: r.data.resultado.resultado });
          dadosRef.current.resultado = r.data.resultado.resultado;
        }
      } catch(e) { console.error('Erro recalcular etapa 7:', e.message); }
    }
    const idAtual = laudoIdRef.current;
    if (idAtual) {
      try {
        const fmtDt = (v) => !v ? null : typeof v === 'string' ? v.substring(0,10) : dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : null;
        const payload = {
          ...dadosRef.current,
          data_inspecao: fmtDt(dadosRef.current.data_inspecao),
          data_th: fmtDt(dadosRef.current.data_th),
          proxima_inspecao_externa: fmtDt(dadosRef.current.proxima_inspecao_externa),
          proxima_inspecao_interna: fmtDt(dadosRef.current.proxima_inspecao_interna),
        };
        // Remover campos cl_ null do payload para nao sobrescrever valores existentes
        const clKeys = ['cl_bases_assentamento','cl_pintura','cl_bocais_soldados','cl_aterramento','cl_fixacao_base','cl_inspecao_solda','cl_chapas','cl_boca_visita','cl_aneis_juntas','cl_iluminacao_emergencia','cl_extintor','cl_teste_hidrostatico','cl_valvulas_manometro','cl_pressostatos','cl_medidor_nivel','cl_termometro','cl_dreno','cl_placa_identificacao','cl_acesso_manutencao','cl_ventilacao_permanente','cl_iluminacao_normas'];
        clKeys.forEach(k => { if (!payload[k]) delete payload[k]; });
        await api.put('/laudos/' + idAtual, { ...payload, _auto: true });
      } catch(e) { console.error('Erro PUT irPara:', e.message); }
    } else if (!criandoRef.current && dadosRef.current.cliente_id && dadosRef.current.tipo_laudo) {
      criandoRef.current = true;
      try {
        const fmtDt2 = (v) => !v ? null : typeof v === 'string' ? v.substring(0,10) : dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : null;
        const payload2 = { ...dadosRef.current, data_inspecao: fmtDt2(dadosRef.current.data_inspecao) };
        const r = await api.post('/laudos', payload2);
        if (r.data.id) laudoIdRef.current = r.data.id;
        if (r.data.id) navigate('/laudos/' + r.data.id + '/editar', { replace: true });
      } catch(e) { console.error('Erro POST irPara:', e.message); }
      finally { criandoRef.current = false; }
    }
  };

  const calcularPMTA = async () => {
    salvarDados();
    try {
      const r = await api.post('/laudos/calcular', { ...dadosRef.current, resultado: null, possui_manometro: true, possui_valvula: true }); // calculo limpo da PMTA: ignora resultado antigo e dispositivos (conferidos na etapa Resultado)
      setCalculo(r.data);
      if (r.data.validades) {
        form.setFieldsValue({
          proxima_inspecao_externa: r.data.validades.proxima_inspecao_externa ? dayjs(r.data.validades.proxima_inspecao_externa) : null,
          proxima_inspecao_interna: r.data.validades.proxima_inspecao_interna ? dayjs(r.data.validades.proxima_inspecao_interna) : null,
        });
        dadosRef.current.proxima_inspecao_externa = r.data.validades.proxima_inspecao_externa;
        dadosRef.current.proxima_inspecao_interna = r.data.validades.proxima_inspecao_interna;
      }
      if (r.data.classificacao) {
        const atual = form.getFieldsValue(['grupo','classe','categoria']);
        const novo = {
          grupo: atual.grupo || String(r.data.classificacao.grupo),
          classe: atual.classe || r.data.classificacao.classe_fluido,
          categoria: atual.categoria || r.data.classificacao.categoria,
        };
        form.setFieldsValue(novo);
        Object.assign(dadosRef.current, novo);
      }
      /* resultado e definido na etapa Resultado */
      if (r.data.recomendacoes) {
        const obsAtual = form.getFieldValue('observacoes') || dadosRef.current.observacoes || '';
        { /* recomendacoes sao geradas na etapa Resultado */ }
      }
      const res = (r.data.resultado && /man[ôo]metro|v[áa]lvula/i.test(r.data.resultado.motivo || '')) ? null : r.data.resultado;
      if (res?.resultado === 'reprovado') message.error(`REPROVADO: ${res.motivo}`);
      else if (res?.resultado === 'apto_com_observacoes') message.warning('APTO COM OBSERVAÇÕES' + (res.motivo ? ': ' + res.motivo : ''));
      else message.success(`PMTA Final: ${String(r.data.pmta_final).replace('.', ',')} kgf/cm²`);
    } catch { message.error('Erro ao calcular PMTA'); }
  };

  const salvar = async (status = 'rascunho') => {
    // Pegar TODOS os valores do formulário agora
    const formValues = form.getFieldsValue(true);
    // Preservar valores do checklist que já estão no dadosRef
    const clKeys = ['cl_bases_assentamento','cl_pintura','cl_bocais_soldados','cl_aterramento','cl_fixacao_base','cl_inspecao_solda','cl_chapas','cl_boca_visita','cl_aneis_juntas','cl_iluminacao_emergencia','cl_extintor','cl_teste_hidrostatico','cl_valvulas_manometro','cl_pressostatos','cl_medidor_nivel','cl_termometro','cl_dreno','cl_placa_identificacao','cl_acesso_manutencao','cl_ventilacao_permanente','cl_iluminacao_normas'];
    const clPreservados = {};
    clKeys.forEach(k => { clPreservados[k] = formValues[k] || dadosRef.current[k] || null; });
    // Filtrar apenas valores nao nulos/undefined do formulario para nao sobrescrever dados existentes
    const formValuesLimpos = {};
    Object.entries(formValues).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') formValuesLimpos[k] = v; });
    dadosRef.current = { ...dadosRef.current, ...formValuesLimpos, ...clPreservados, possui_manometro: possuiManometro, possui_valvula: possuiValvula };
    try {
      const d = dadosRef.current;
      const fmtDt = (v) => !v ? null : typeof v === 'string' ? v.substring(0,10) : dayjs.isDayjs(v) ? v.format('YYYY-MM-DD') : null;
      const payload = {
        ...d,
        status,
        possui_manometro: possuiManometro,
        possui_valvula: possuiValvula,
        data_inspecao: fmtDt(d.data_inspecao),
        data_th: fmtDt(d.data_th),
        proxima_inspecao_externa: fmtDt(d.proxima_inspecao_externa),
        proxima_inspecao_interna: fmtDt(d.proxima_inspecao_interna),
      };
      console.log('isEdicao:', isEdicao, 'id:', id);
      console.log('Payload resultado:', payload.resultado, 'possui_manometro:', payload.possui_manometro);
      const idAtual = laudoIdRef.current;
      if (idAtual) {
        const resp = await api.put(`/laudos/${idAtual}`, payload);
        console.log('Resposta PUT:', resp.data?.resultado, resp.data?.possui_manometro);
        message.success('Laudo atualizado! Resultado: ' + resp.data?.resultado);
        navigate(`/laudos/${idAtual}`);
      } else {
        if (criandoRef.current) return;
        criandoRef.current = true;
        const r = await api.post('/laudos', payload);
        criandoRef.current = false;
        laudoIdRef.current = r.data.id;
        message.success('Laudo criado com sucesso! Código: ' + r.data.codigo);
        navigate(`/laudos/${r.data.id}`);
      }
    } catch (e) {
      criandoRef.current = false;
      console.error('Erro ao salvar:', e);
      message.error('Erro ao salvar laudo: ' + (e.response?.data?.erro || e.message));
    }
  };

  const CLItem = ({ campo }) => (
    <Form.Item name={campo.key} style={{ marginBottom: 4 }}>
      <Radio.Group>
        <Radio value="C" style={{ color: 'green' }}>C</Radio>
        <Radio value="NC" style={{ color: 'red' }}>N/C</Radio>
        <Radio value="NA" style={{ color: 'gray' }}>N/A</Radio>
      </Radio.Group>
    </Form.Item>
  );

  const fmt = (v, d = 2) => v != null ? String(parseFloat(v).toFixed(d)).replace('.', ',') : '-';
  const fmtData = (v) => v ? (typeof v === 'string' ? new Date(v).toLocaleDateString('pt-BR') : v.format('DD/MM/YYYY')) : '-';
  const fmtBool = (v) => v ? 'Sim' : 'Não';

  const d = dadosRef.current;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/laudos')}>Voltar</Button>
        <Title level={4} style={{ margin: 0 }}>{isEdicao ? 'Editar Laudo' : 'Novo Laudo'}</Title>
      </div>

      <Steps current={step} items={STEPS.map((t, i) => ({ title: t }))} style={{ marginBottom: 16 }} size="small"
        onChange={(s) => irPara(s)} />

      <Form form={form} layout="vertical" preserve={true}>

        {/* STEP 0: IDENTIFICAÇÃO */}
        <div style={{ display: step === 0 ? 'block' : 'none' }}>
          <Card title="Identificação Geral" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Cliente" rules={(osIdFromUrl || veioDaOS) ? [] : [{ required: true }]}>
                  {(osIdFromUrl || veioDaOS) ? (
                    <Input disabled value={nomeClienteLaudo} />
                  ) : (
                    <Select showSearch optionFilterProp="children" placeholder="Selecione o cliente" onChange={selecionarCliente}>
                      {clientes.map(c => <Option key={c.id} value={c.id}>{c.codigo} — {c.razao_social || c.nome}</Option>)}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="cnpj_laudo_id" label="CNPJ do Laudo" extra={(osIdFromUrl || veioDaOS || form.getFieldValue('numero_os')) ? 'Definido na OS — para trocar, edite a OS.' : null}>
                  <Select
                    disabled={!!(osIdFromUrl || veioDaOS || form.getFieldValue('numero_os')) /* cnpjTravadoOS */}
                    showSearch
                    allowClear
                    placeholder="Pesquise pelo CNPJ"
                    optionFilterProp="children"
                    filterOption={(input, option) => option.children?.toString().toLowerCase().includes(input.toLowerCase())}
                    onChange={(val) => {
                      const cnpj = cnpjs.find(c => c.id === val);
                      setArtFilial(cnpj?.art_numero || '');
                    }}
                  >
                    {(todosCnpjs.length > 0 ? todosCnpjs : cnpjs).map(c => <Option key={c.id} value={c.id}>{c.cnpj} — {c.razao_social} {c.cliente_nome ? '(' + c.cliente_nome + ')' : ''}</Option>)}
                  </Select>
                </Form.Item>
              </Col>
              {artFilial && <Col span={8}><Form.Item label="ART do Cliente (CREA-MG)"><Input value={artFilial} disabled style={{background:'#f6ffed', color:'#135200', fontWeight:'bold'}} /></Form.Item></Col>}
              <Col span={8}><Form.Item name="tipo_laudo" label="Tipo de Laudo" rules={[{ required: true }]}><Select disabled={!!osIdFromUrl || veioDaOS}>{TIPOS_LAUDO.map(t => <Option key={t} value={t}>{t}</Option>)}</Select></Form.Item></Col>
            </Row>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item name="numero_os" label="Número OS">
                  <Input placeholder="OS-001" disabled={!!osIdFromUrl || veioDaOS} />
                </Form.Item>
              </Col>
              <Col span={6}><Form.Item name="data_inspecao" label="Data da Inspeção" required><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
              <Col span={6}><Form.Item name="tecnico" label="Técnico Responsável"><Input disabled={!!osIdFromUrl || veioDaOS} /></Form.Item></Col>
            </Row>
            {/* Campos hidden para preservar valores da OS */}
            <Form.Item name="numero_os" hidden><input /></Form.Item>
            <Form.Item name="tecnico" hidden><input /></Form.Item>
            <Form.Item name="cliente_id" hidden><input /></Form.Item>
            <Form.Item name="cnpj_laudo_id" hidden><input /></Form.Item>
            {(osIdFromUrl || veioDaOS) && (
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item name="endereco_atendimento" label="Endereço de Atendimento">
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Card>

        </div>

        {/* STEP 1: EQUIPAMENTO */}
        <div style={{ display: step === 1 ? 'block' : 'none' }}>
          <Card title="Identificação do Equipamento" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}><Form.Item name="tipo_equipamento" label="Tipo de Equipamento"><AutoComplete placeholder="Escolha da lista ou digite" options={['Autoclave','Gerador de Vapor','Vaso de Pressão','Acumulador Hidráulico','Boiler','Panelão'].map(t => ({ value: t }))} filterOption={(e, o) => String(o.value).toLowerCase().includes(String(e).toLowerCase())} /></Form.Item></Col>
              <Col span={8}><Form.Item name="tipo_inspecao_periodicidade" label="Tipo de Inspeção"><Select><Option value="inicial">Inicial</Option><Option value="periodica">Periódica</Option><Option value="extraordinaria">Extraordinária</Option></Select></Form.Item></Col>
              <Col span={8}><Form.Item name="tipo_inspecao_modo" label="Modo" tooltip="Definido na hora de gerar o laudo a partir da OS e não muda depois. A inspeção externa é um laudo próprio."><Select disabled><Option value="interna">Interna</Option><Option value="interna_externa">Interna e Externa</Option></Select></Form.Item></Col>
            </Row>
            <Row gutter={16}>
              <Col span={4}><Form.Item name="tag" label="TAG" normalize={v => (v || '').toUpperCase().replace(/^\s+/, '').replace(/\s\s+/g, ' ')}><Input placeholder="VP-01" onBlur={e => form.setFieldsValue({ tag: (e.target.value || '').trim() })} /></Form.Item></Col>
              <Col span={4}><Form.Item name="posicao" label="Posição"><Select><Option value="vertical">Vertical</Option><Option value="horizontal">Horizontal</Option></Select></Form.Item></Col>
              <Col span={8}><Form.Item name="numero_serie" label="Número de Série"><Input /></Form.Item></Col>
              <Col span={8}><Form.Item name="fabricante" label="Fabricante"><Input /></Form.Item></Col>
            </Row>
            <Row gutter={16}>
              <Col span={4}><Form.Item name="fluido" label="Fluido"><Select showSearch optionFilterProp="children" onChange={v => { const fl = fluidosNR13.find(f => f.nome === v); if (fl) form.setFieldsValue({ fluido_outro: '' }); }}>{fluidosNR13.map(f => <Option key={f.id} value={f.nome}>{f.nome} (Classe {f.classe})</Option>)}<Option value="outro">Outro</Option></Select></Form.Item></Col>
              <Col span={4}><Form.Item name="fluido_outro" label="Fluido (outro)"><Input /></Form.Item></Col>
              <Col span={4}><Form.Item name="temperatura" label="Temperatura (°C)"><InputNumber style={{ width: '100%' }} decimalSeparator="," /></Form.Item></Col>
              <Col span={4}><Form.Item name="ano_fabricacao" label="Ano Fabricação"><InputNumber style={{ width: '100%' }} min={1900} max={2100} /></Form.Item></Col>
              <Col span={4}><Form.Item name="modelo" label="Modelo"><Input /></Form.Item></Col>
              <Col span={4}><Form.Item name="asm" label="ASME VIII, DIV. I"><Input placeholder="Ex: tabela 2021" /></Form.Item></Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}><Form.Item name="local_instalacao" label="Local de Instalação"><Input /></Form.Item></Col>
            </Row>
            <Row gutter={16}>
              <Col span={6}><Form.Item name="instalacao_aberta" label="Instalação"><Select><Option value={true}>Aberta</Option><Option value={false}>Fechada</Option></Select></Form.Item></Col>
              <Col span={6}><Form.Item name="duas_saidas" label="Duas saídas amplas?"><Select><Option value={true}>Sim</Option><Option value={false}>Não</Option></Select></Form.Item></Col>
              <Col span={6}><Form.Item name="equipamento_movel" label="Equipamento Móvel?"><Select><Option value={true}>Sim</Option><Option value={false}>Não</Option></Select></Form.Item></Col>
              <Col span={6}><Form.Item name="possui_livro" label="Possui livro?"><Select><Option value={true}>Sim</Option><Option value={false}>Não</Option></Select></Form.Item></Col>
            </Row>

            <Row gutter={16}>
              <Col span={6}><Form.Item name="possui_prontuario" label="Possui prontuário?"><Select><Option value={true}>Sim</Option><Option value={false}>Não</Option></Select></Form.Item></Col>
              <Col span={6}><Form.Item name="enviar_livro" label="Enviar livro?"><Select><Option value={true}>Sim</Option><Option value={false}>Não</Option></Select></Form.Item></Col>
              <Col span={6}><Form.Item name="enviar_prontuario" label="Enviar prontuário?"><Select><Option value={true}>Sim</Option><Option value={false}>Não</Option></Select></Form.Item></Col>
            </Row>
          </Card>
        </div>

        {/* STEP 2: DIMENSIONAL */}
        <div style={{ display: step === 2 ? 'block' : 'none' }}>
          <Card title="Dimensional / Medição de Espessura" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}><Form.Item name="medida_circunferencial" label="Medida Circunferencial (mm)"><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
              <Col span={8}><Form.Item name="diametro_externo" label="Diâmetro Externo (mm)"><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
              <Col span={8}><Form.Item name="distancia_solda_solda" label="Distância Solda a Solda (mm)"><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
            </Row>
            <Row gutter={16}>
              <Col span={4}><Form.Item name="volume" label="Volume (deixe vazio para calcular)" tooltip="Se ficar em branco, o sistema calcula pelo diâmetro interno, pelo comprimento solda a solda e pelo formato dos tampos."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
              <Col span={4}><Form.Item name="volume_unidade" label="Unidade" initialValue="L"><Select><Option value="L">Litros</Option><Option value="m3">m³</Option></Select></Form.Item></Col>
              <Col span={16}><Text type="secondary" style={{ fontSize: 12 }}>O volume entra no P×V do enquadramento da NR-13. Em branco, é calculado com o raio interno mais o volume dos dois tampos.</Text></Col>
            </Row>
            <Divider orientation="left">Radiografia</Divider>
            <Row gutter={16}>
              <Col span={8}><Form.Item name="radiografado" label="Esse equipamento foi radiografado?" valuePropName="checked"><Switch checkedChildren="Sim" unCheckedChildren="Não" /></Form.Item></Col>
            </Row>
            <Divider orientation="left">Material e coeficientes do costado</Divider>
            <Row gutter={16}>
              <Col span={8}><Form.Item name="material_tensao_id" label="Material do costado" tooltip="Define a tensão admissível S do costado. Escolha o material e a tensão é preenchida ao lado — se digitar outro valor por cima, vale o que você digitou." rules={[{ required: true, message: 'Selecione o material' }]}>
                <Select allowClear showSearch optionFilterProp="children" placeholder="Selecione o material" onChange={(val) => {
                  const mat = materiaisTensao.find(m => m.id === val);
                  if (mat) {
                    const kgf = parseFloat((parseFloat(mat.tensao_mpa) * 10.197162).toFixed(2));
                    form.setFieldsValue({ tensao: kgf, material: mat.nome });
                    salvarDados();
                  }
                }}>
                  {materiaisTensao.map(m => <Option key={m.id} value={m.id}>{m.nome} — {(parseFloat(m.tensao_mpa)*10.197162).toFixed(0)} kgf/cm²</Option>)}
                </Select>
              </Form.Item></Col>
              <Col span={4}><Form.Item name="tensao" label="Tensão S — costado" tooltip="Tensão admissível do material do costado. Preenchida pelo material escolhido; pode ser editada."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
              <Col span={3}><Form.Item name="coef_c" label="Coef. E — costado" tooltip="Eficiência da junta soldada do costado: 1,00 com radiografia total, 0,85 parcial, 0,70 sem radiografia. Em branco, o cálculo usa 0,7."><InputNumber style={{ width: '100%' }} precision={4} step={0.1} decimalSeparator="," /></Form.Item></Col>
            </Row>
            <Divider orientation="left">Espessura do Casco — 5 leituras (mm)</Divider>
            <Row gutter={16}>{[1,2,3,4,5].map(i => <Col span={4} key={i}><Form.Item name={`esp_casco_${i}`} label={`Leitura ${i}`}><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>)}</Row>
            <Divider orientation="left">Tampo Superior/Esquerdo — 5 leituras (mm)</Divider>
            <Row gutter={12}>
              <Col span={4}><Form.Item name="tipo_tampo_sup" label="Tipo de tampo" tooltip="O formato decide a fórmula da PMTA: semi-elíptico e torisférico pelo UG-32, semiesférico pelo UG-32(f), cônico pelo UG-32(g) e plano pelo UG-34."><Select>{['semi-elíptico','semiesférico','plano','cônico','torisférico'].map(t => <Option key={t} value={t}>{t}</Option>)}</Select></Form.Item></Col>
              <Col span={6}><Form.Item name="material_tampo_sup_id" label="Material" tooltip="Preenche a tensão admissível S deste tampo. Deixe vazio se o tampo for do mesmo material do costado.">
                <Select allowClear showSearch optionFilterProp="children" placeholder="mesmo do costado" onChange={(val) => {
                  const mat = materiaisTensao.find(m => m.id === val);
                  if (mat) { form.setFieldsValue({ tensao_tampo_sup: parseFloat((parseFloat(mat.tensao_mpa) * 10.197162).toFixed(2)) }); salvarDados(); }
                }}>
                  {materiaisTensao.map(m => <Option key={m.id} value={m.id}>{m.nome} — {(parseFloat(m.tensao_mpa)*10.197162).toFixed(0)} kgf/cm²</Option>)}
                </Select>
              </Form.Item></Col>
              <Col span={3}><Form.Item name="coef_tampo_sup" label="Coef. E" tooltip="Eficiência da junta soldada: 1,00 com radiografia total, 0,85 parcial, 0,70 sem radiografia. Vazio = 0,7, exceto no tampo plano de chapa inteiriça, onde vale 1,0."><InputNumber style={{ width: '100%' }} precision={4} step={0.05} decimalSeparator="," /></Form.Item></Col>
              <Col span={3}><Form.Item name="tensao_tampo_sup" label="Tensão S" tooltip="Tensão admissível em kgf/cm². Preenchida pelo material escolhido ao lado; o valor digitado sempre prevalece. Vazio = usa a do costado."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const tp = String(getFieldValue('tipo_tampo_sup') || '').toLowerCase();
                  const c = [];
                  if (tp.indexOf('elip') >= 0 || tp.indexOf('elíp') >= 0) {
                    c.push(<Col span={5} key="rel"><Form.Item name="relacao_tampo_sup" label="Relação" tooltip="Relação entre o diâmetro e a profundidade da parte curva (sem a aba reta). No 2:1 a profundidade é 1/4 do diâmetro; no 3:1 é 1/6. Tampo mais raso, PMTA menor."><Select allowClear><Option value="2:1">2:1</Option><Option value="3:1">3:1</Option><Option value="outro">outro</Option></Select></Form.Item></Col>);
                    if (getFieldValue('relacao_tampo_sup') === 'outro') c.push(<Col span={3} key="h"><Form.Item name="prof_tampo_sup" label="h (mm)" tooltip="Profundidade: altura da parte curva, do início da curvatura até o ponto mais alto. Não inclua a aba reta."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>);
                  }
                  if (tp.indexOf('toris') >= 0) {
                    c.push(<Col span={5} key="geo"><Form.Item name="geom_toris_sup" label="Geometria" tooltip="O torisférico é uma calota de raio L fechada por um canto de raio r. Cada padrão tem a sua proporção. Vazio = ASME, o mais conservador."><Select allowClear><Option value="asme6">ASME F&amp;D (6%)</Option><Option value="kloepper">Klöpper DIN 28011</Option><Option value="korbbogen">Korbbogen DIN 28013</Option><Option value="manual">medir L e r</Option></Select></Form.Item></Col>);
                    if (getFieldValue('geom_toris_sup') === 'manual') {
                      c.push(<Col span={3} key="L"><Form.Item name="raio_coroa_sup" label="L (mm)" tooltip="Raio interno da parte central abaulada (a calota)."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>);
                      c.push(<Col span={3} key="r"><Form.Item name="raio_canto_sup" label="r (mm)" tooltip="Raio da dobra entre a calota e a aba reta. Canto mais fechado, PMTA menor."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>);
                    }
                  }
                  if (tp.indexOf('plano') >= 0) {
                    c.push(<Col span={6} key="fix"><Form.Item name="fixacao_plano_sup" label="Fixação" tooltip="Como o tampo está preso no costado — define o coeficiente C do UG-34. Quanto mais engastada a ligação, maior a PMTA. Na dúvida use solda de filete, que é o caso mais desfavorável. Vazio = solda de topo."><Select allowClear><Option value="integral">integral / forjado</Option><Option value="topo">solda de topo</Option><Option value="plug">encaixado (plug)</Option><Option value="filete">solda de filete</Option><Option value="aparafusado">aparafusado</Option><Option value="outro">outro — informar C</Option></Select></Form.Item></Col>);
                    if (getFieldValue('fixacao_plano_sup') === 'outro') c.push(<Col span={2} key="c"><Form.Item name="c_plano_sup" label="C" tooltip="Coeficiente C da Figura UG-34 para este caso de fixação."><InputNumber style={{ width: '100%' }} precision={3} step={0.01} decimalSeparator="," /></Form.Item></Col>);
                  }
                  if (tp.indexOf('conic') >= 0 || tp.indexOf('cônic') >= 0) {
                    c.push(<Col span={4} key="x"><Form.Item name="medida_x_sup" label="Medida X (mm)" tooltip="Geratriz do cone: o lado inclinado, medido do canto até o vértice, em milímetros. Não é o ângulo — o sistema calcula o ângulo a partir dela e do raio interno."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>);
                  }
                  return c.length ? <>{c}</> : null;
                }}
              </Form.Item>
            </Row>
            <Row gutter={16}>{[1,2,3,4,5].map(i => <Col span={4} key={i}><Form.Item name={`esp_tampo_${i}`} label={`Leitura ${i}`}><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>)}</Row>
            <Divider orientation="left">Tampo Inferior/Direito — 5 leituras (mm)</Divider>
            <Row gutter={12}>
              <Col span={4}><Form.Item name="tipo_tampo_inf" label="Tipo de tampo" tooltip="O formato decide a fórmula da PMTA: semi-elíptico e torisférico pelo UG-32, semiesférico pelo UG-32(f), cônico pelo UG-32(g) e plano pelo UG-34."><Select>{['semi-elíptico','semiesférico','plano','cônico','torisférico'].map(t => <Option key={t} value={t}>{t}</Option>)}</Select></Form.Item></Col>
              <Col span={6}><Form.Item name="material_tampo_inf_id" label="Material" tooltip="Preenche a tensão admissível S deste tampo. Deixe vazio se o tampo for do mesmo material do costado.">
                <Select allowClear showSearch optionFilterProp="children" placeholder="mesmo do costado" onChange={(val) => {
                  const mat = materiaisTensao.find(m => m.id === val);
                  if (mat) { form.setFieldsValue({ tensao_tampo_inf: parseFloat((parseFloat(mat.tensao_mpa) * 10.197162).toFixed(2)) }); salvarDados(); }
                }}>
                  {materiaisTensao.map(m => <Option key={m.id} value={m.id}>{m.nome} — {(parseFloat(m.tensao_mpa)*10.197162).toFixed(0)} kgf/cm²</Option>)}
                </Select>
              </Form.Item></Col>
              <Col span={3}><Form.Item name="coef_tampo_inf" label="Coef. E" tooltip="Eficiência da junta soldada: 1,00 com radiografia total, 0,85 parcial, 0,70 sem radiografia. Vazio = 0,7, exceto no tampo plano de chapa inteiriça, onde vale 1,0."><InputNumber style={{ width: '100%' }} precision={4} step={0.05} decimalSeparator="," /></Form.Item></Col>
              <Col span={3}><Form.Item name="tensao_tampo_inf" label="Tensão S" tooltip="Tensão admissível em kgf/cm². Preenchida pelo material escolhido ao lado; o valor digitado sempre prevalece. Vazio = usa a do costado."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const tp = String(getFieldValue('tipo_tampo_inf') || '').toLowerCase();
                  const c = [];
                  if (tp.indexOf('elip') >= 0 || tp.indexOf('elíp') >= 0) {
                    c.push(<Col span={5} key="rel"><Form.Item name="relacao_tampo_inf" label="Relação" tooltip="Relação entre o diâmetro e a profundidade da parte curva (sem a aba reta). No 2:1 a profundidade é 1/4 do diâmetro; no 3:1 é 1/6. Tampo mais raso, PMTA menor."><Select allowClear><Option value="2:1">2:1</Option><Option value="3:1">3:1</Option><Option value="outro">outro</Option></Select></Form.Item></Col>);
                    if (getFieldValue('relacao_tampo_inf') === 'outro') c.push(<Col span={3} key="h"><Form.Item name="prof_tampo_inf" label="h (mm)" tooltip="Profundidade: altura da parte curva, do início da curvatura até o ponto mais alto. Não inclua a aba reta."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>);
                  }
                  if (tp.indexOf('toris') >= 0) {
                    c.push(<Col span={5} key="geo"><Form.Item name="geom_toris_inf" label="Geometria" tooltip="O torisférico é uma calota de raio L fechada por um canto de raio r. Cada padrão tem a sua proporção. Vazio = ASME, o mais conservador."><Select allowClear><Option value="asme6">ASME F&amp;D (6%)</Option><Option value="kloepper">Klöpper DIN 28011</Option><Option value="korbbogen">Korbbogen DIN 28013</Option><Option value="manual">medir L e r</Option></Select></Form.Item></Col>);
                    if (getFieldValue('geom_toris_inf') === 'manual') {
                      c.push(<Col span={3} key="L"><Form.Item name="raio_coroa_inf" label="L (mm)" tooltip="Raio interno da parte central abaulada (a calota)."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>);
                      c.push(<Col span={3} key="r"><Form.Item name="raio_canto_inf" label="r (mm)" tooltip="Raio da dobra entre a calota e a aba reta. Canto mais fechado, PMTA menor."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>);
                    }
                  }
                  if (tp.indexOf('plano') >= 0) {
                    c.push(<Col span={6} key="fix"><Form.Item name="fixacao_plano_inf" label="Fixação" tooltip="Como o tampo está preso no costado — define o coeficiente C do UG-34. Quanto mais engastada a ligação, maior a PMTA. Na dúvida use solda de filete, que é o caso mais desfavorável. Vazio = solda de topo."><Select allowClear><Option value="integral">integral / forjado</Option><Option value="topo">solda de topo</Option><Option value="plug">encaixado (plug)</Option><Option value="filete">solda de filete</Option><Option value="aparafusado">aparafusado</Option><Option value="outro">outro — informar C</Option></Select></Form.Item></Col>);
                    if (getFieldValue('fixacao_plano_inf') === 'outro') c.push(<Col span={2} key="c"><Form.Item name="c_plano_inf" label="C" tooltip="Coeficiente C da Figura UG-34 para este caso de fixação."><InputNumber style={{ width: '100%' }} precision={3} step={0.01} decimalSeparator="," /></Form.Item></Col>);
                  }
                  if (tp.indexOf('conic') >= 0 || tp.indexOf('cônic') >= 0) {
                    c.push(<Col span={4} key="x"><Form.Item name="medida_x_inf" label="Medida X (mm)" tooltip="Geratriz do cone: o lado inclinado, medido do canto até o vértice, em milímetros. Não é o ângulo — o sistema calcula o ângulo a partir dela e do raio interno."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>);
                  }
                  return c.length ? <>{c}</> : null;
                }}
              </Form.Item>
            </Row>
            <Row gutter={16}>{[1,2,3,4,5].map(i => <Col span={4} key={i}><Form.Item name={`esp_tampo_inf_${i}`} label={`Leitura ${i}`}><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>)}</Row>
            <Divider orientation="left">Dados de Fábrica e Cálculo</Divider>
            <Row gutter={16}>
              <Col span={5}><Form.Item name="cos_fabrica" label="Esp. nominal — costado (mm)" tooltip="Espessura original de fábrica do costado, do prontuário ou da placa. Serve de referência para comparar com o que foi medido."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
              <Col span={5}><Form.Item name="tam_fabrica" label="Esp. nominal — tampo sup./esq. (mm)" tooltip="Espessura original de fábrica do tampo superior/esquerdo."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
              <Col span={5}><Form.Item name="tam_fabrica_inf" label="Esp. nominal — tampo inf./dir. (mm)" tooltip="Espessura original de fábrica do tampo inferior/direito."><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
            </Row>
            <Divider orientation="left">Pressões</Divider>
            <Row gutter={16}>
              <Col span={5}><Form.Item name="pressao_operacao" label="Pressão de Operação (PO)"><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
              <Col span={3}><Form.Item name="pressao_operacao_unidade" label="Unidade" initialValue="kgf"><Select><Option value="kgf">kgf/cm²</Option><Option value="psi">psi</Option><Option value="bar">bar</Option><Option value="mpa">MPa</Option></Select></Form.Item></Col>
              <Col span={5}><Form.Item name="pmta_fabricante" label="PMTA do Fabricante"><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
              <Col span={3}><Form.Item name="pmta_fabricante_unidade" label="Unidade" initialValue="kgf"><Select><Option value="kgf">kgf/cm²</Option><Option value="psi">psi</Option><Option value="bar">bar</Option><Option value="mpa">MPa</Option></Select></Form.Item></Col>

            </Row>
            <Divider orientation="left">Teste Hidrostático</Divider>
            <Row gutter={16} align="stretch" style={{ marginBottom: 12 }}>{/* TH lado a lado */}
              <Col span={12}>
            <div style={{ background: '#f6f8ff', border: '1px solid #d0d7ff', borderRadius: 8, padding: 12, height: '100%' }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>TH anterior (fabricante ou outra inspetora)</Text>
              <Row gutter={16}>
                <Col span={7}><Form.Item name="th_fabrica_possui" label="Possui registro?" tooltip="Qualquer Teste Hidrostático anterior a esta inspeção — do fabricante ou de outra inspetora."><Select><Option value={true}>Sim</Option><Option value={false}>Não</Option></Select></Form.Item></Col>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.th_fabrica_possui !== curr.th_fabrica_possui}>
                  {({ getFieldValue }) => getFieldValue('th_fabrica_possui') ? (<>
                    <Col span={6}><Form.Item name="th_fabricante" label="Pressão do TH"><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
                    <Col span={5}><Form.Item name="th_fabrica_unidade" label="Unidade" initialValue="kgf"><Select><Option value="kgf">kgf/cm²</Option><Option value="psi">psi</Option><Option value="bar">bar</Option><Option value="mpa">MPa</Option></Select></Form.Item></Col>
                    <Col span={6}><Form.Item name="th_fabrica_ano" label="Ano do TH"><InputNumber style={{ width: '100%' }} min={1900} max={2100} /></Form.Item></Col>
                  </>) : null}
                </Form.Item>
              </Row>
            </div>
              </Col>
              <Col span={12}>
            <div style={{ background: '#f6fff0', border: '1px solid #b7eb8f', borderRadius: 8, padding: 12, height: '100%' }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>TH Aplicado pela Celum</Text>
              <Row gutter={16}>
                <Col span={7}><Form.Item name="th_celum_aplicado" label="Foi aplicado TH pela Celum?"><Select><Option value={true}>Sim</Option><Option value={false}>Não</Option></Select></Form.Item></Col>
                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.th_celum_aplicado !== curr.th_celum_aplicado}>
                  {({ getFieldValue }) => getFieldValue('th_celum_aplicado') ? (<>
                    <Col span={6}><Form.Item name="th_celum_pressao" label="Pressão de Teste (Celum)"><InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," /></Form.Item></Col>
                    <Col span={5}><Form.Item name="th_celum_unidade" label="Unidade" initialValue="kgf"><Select><Option value="kgf">kgf/cm²</Option><Option value="psi">psi</Option><Option value="bar">bar</Option><Option value="mpa">MPa</Option></Select></Form.Item></Col>
                    <Col span={6}><Form.Item name="data_th" label="Data do TH"><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
                  </>) : null}
                </Form.Item>
              </Row>
            </div>
              </Col>
            </Row>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const temFabrica = getFieldValue('th_fabrica_possui');
                const temCelum = getFieldValue('th_celum_aplicado');
                if (!temFabrica && !temCelum) {
                  return <div style={{ padding: 8, background: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 6, marginBottom: 12 }}><Text type="warning">⚠️ Equipamento sem registro de TH — será reprovado por falta de TH.</Text></div>;
                }
                return null;
              }}
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="th_texto_opcao" label="Texto sobre TH no relatório" initialValue="sem_registro">
                  <Select onChange={(v) => { if (v !== 'custom') form.setFieldsValue({ th_texto_custom: '' }); }}>
                    <Option value="sem_registro">Sem registro — não submetido (padrão)</Option>
                    <Option value="com_registro">Possui registro de TH anterior — não submetido por nós</Option>
                    <Option value="registro_antigo">Último TH registrado com mais de 10 anos — recomendar novo TH</Option>
                    <Option value="realizado">Submetido por nós ao TH</Option>
                    <Option value="custom">Texto personalizado</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.th_texto_opcao !== curr.th_texto_opcao}>
              {({ getFieldValue }) => getFieldValue('th_texto_opcao') === 'custom' ? (
                <Row gutter={16}><Col span={24}><Form.Item name="th_texto_custom" label="Texto personalizado do TH"><Input.TextArea rows={3} /></Form.Item></Col></Row>
              ) : null}
            </Form.Item>
            <Button type="primary" icon={<CalculatorOutlined />} onClick={calcularPMTA} style={{ marginBottom: 12 }}>Calcular PMTA</Button>

            {calculo && (
              <Alert type={calculo.resultado?.resultado === 'reprovado' ? 'error' : calculo.resultado?.resultado === 'apto_com_observacoes' ? 'warning' : 'success'} style={{ marginTop: 8 }} message={
                <div>
                  <Row gutter={16}>
                    <Col span={8}><Text strong>PMTA Costado:</Text> {fmt(calculo.pmta_casco)} kgf/cm² <Text type="secondary">(esp. {fmt(calculo.esp_casco_encontrada)} mm)</Text></Col>
                    <Col span={8}><Text strong>PMTA Tampo Sup./Esq.:</Text> {fmt(calculo.pmta_tampo_sup)} kgf/cm² <Text type="secondary">({calculo.tipo_tampo_sup_txt ? calculo.tipo_tampo_sup_txt + ' — ' : ''}esp. {fmt(calculo.esp_tampo_sup_encontrada)} mm)</Text></Col>
                    <Col span={8}><Text strong>PMTA Tampo Inf./Dir.:</Text> {fmt(calculo.pmta_tampo_inf)} kgf/cm² <Text type="secondary">({calculo.tipo_tampo_inf_txt ? calculo.tipo_tampo_inf_txt + ' — ' : ''}esp. {fmt(calculo.esp_tampo_inf_encontrada)} mm)</Text></Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 6 }}>
                    <Col span={19}><Text strong style={{ fontSize: 15 }}>PMTA Calculada: {fmt(calculo.pmta_final)} kgf/cm²{calculo.pmta_origem ? ' (menor valor — ' + calculo.pmta_origem + ')' : ''} | {fmt(calculo.pmta_final/0.070307)} psi | {fmt(calculo.pmta_final/1.01972)} bar | {fmt(calculo.pmta_final/10.1972,3)} MPa</Text></Col>
                    <Col span={5}><Text strong>Diâm. Interno:</Text> {fmt(calculo.diametro_interno)} mm</Col>
                  </Row>
                  {calculo.pmta_pendencias && calculo.pmta_pendencias.length > 0 && (
                    <div style={{ marginTop: 8 }}><Text strong type="danger">Pendências do cálculo: </Text><Text type="danger">{calculo.pmta_pendencias.join('  |  ')}</Text></div>
                  )}
                  {calculo.pmta_avisos && calculo.pmta_avisos.length > 0 && (
                    <div style={{ marginTop: 4 }}><Text strong style={{ color: '#d46b08' }}>Avisos: </Text><Text style={{ color: '#d46b08' }}>{calculo.pmta_avisos.join('  |  ')}</Text></div>
                  )}
                  {calculo.memoria_casco && (
                    <div style={{ marginTop: 8, fontSize: 12, color: '#555', lineHeight: 1.5 }}>
                      <div><b>Memória de cálculo</b>{calculo.origem_diametro ? '  —  diâmetro interno: ' + calculo.origem_diametro : ''}</div>
                      <div>{calculo.formula_casco}</div>
                      <div>{calculo.memoria_casco}</div>
                      {calculo.memoria_tampo_sup && <div style={{ marginTop: 4 }}>{calculo.formula_tampo_sup}</div>}
                      {calculo.memoria_tampo_sup && <div>{calculo.memoria_tampo_sup}</div>}
                      {calculo.memoria_tampo_inf && <div style={{ marginTop: 4 }}>{calculo.formula_tampo_inf}</div>}
                      {calculo.memoria_tampo_inf && <div>{calculo.memoria_tampo_inf}</div>}
                      {calculo.volume_memoria && <div style={{ marginTop: 4 }}>Volume: {calculo.volume_memoria}</div>}
                    </div>
                  )}
                  <Row gutter={16} style={{ marginTop: 6 }}>
                    <Col span={5}><Text>Esp. Casco: {fmt(calculo.esp_casco_encontrada)} mm</Text></Col>
                    <Col span={5}><Text>Esp. Tampos: {fmt(calculo.esp_tampo_sup_encontrada)} / {fmt(calculo.esp_tampo_inf_encontrada)} mm</Text></Col>
                    <Col span={4}><Text>Raio: {fmt(calculo.raio)} mm</Text></Col>
                    {calculo.volume_calculado > 0 && <Col span={4}><Text>Volume: {fmt(calculo.volume_l || calculo.volume_calculado)} L</Text></Col>}
                  </Row>
                  {calculo.pv?.PV_classificacao > 0 && (
                    <Row gutter={16} style={{ marginTop: 6 }}>
                      <Col span={8}><Text>P×V enquadramento: {fmt(calculo.pv.PV_enquadramento)} kPa·m³</Text></Col>
                      <Col span={8}><Text>P×V classificação: {fmt(calculo.pv.PV_classificacao, 3)} MPa·m³</Text></Col>
                      {calculo.classificacao && <Col span={8}><Text strong>Grupo {calculo.classificacao.grupo} | Classe {calculo.classificacao.classe_fluido} | Cat. {calculo.classificacao.categoria}</Text></Col>}
                    </Row>
                  )}
                  {calculo.resultado?.resultado === 'reprovado' && <Row style={{ marginTop: 6 }}><Col><Text type="danger" strong>⚠️ {calculo.resultado.motivo}</Text></Col></Row>}
                  {calculo.resultado?.resultado === 'apto_com_observacoes' && <Row style={{ marginTop: 6 }}><Col><Text style={{ color: '#d46b08' }} strong>⚠️ {calculo.resultado.motivo}</Text></Col></Row>}
                  {calculo.validades?.proxima_inspecao_externa && (
                    <Row gutter={16} style={{ marginTop: 6 }}>
                      <Col span={12}><Text>Próx. Ext.: <strong>{new Date(calculo.validades.proxima_inspecao_externa).toLocaleDateString('pt-BR')}</strong> ({calculo.validades.prazo_externo_anos} anos)</Text></Col>
                      <Col span={12}><Text>Próx. Int.: <strong>{calculo.validades.proxima_inspecao_interna ? new Date(calculo.validades.proxima_inspecao_interna).toLocaleDateString('pt-BR') : '-'}</strong> ({calculo.validades.prazo_interno_anos} anos)</Text></Col>
                    </Row>
                  )}
                </div>
              } />
            )}
          
            <div style={{ marginTop: 12, padding: 12, background: '#f0f4ff', border: '1px solid #adc6ff', borderRadius: 8 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>PMTA Final (fornecida pelo engenheiro):</Text>
              <Form.Item noStyle shouldUpdate>{() => {
                const conv = { kgf: 1, psi: 0.070307, bar: 1.01972, mpa: 10.1972 };
                const manual = parseFloat(form.getFieldValue('pmta_final_manual')) * (conv[form.getFieldValue('pmta_final_manual_unidade') || 'kgf'] || 1);
                const fab = parseFloat(form.getFieldValue('pmta_fabricante')) * (conv[form.getFieldValue('pmta_fabricante_unidade') || 'kgf'] || 1);
                if (!(manual > 0 && fab > 0 && manual > fab * 1.0001)) return null;
                return <Alert type="warning" showIcon style={{ marginBottom: 8 }}
                  message={'PMTA informada (' + manual.toFixed(2).replace('.', ',') + ' kgf/cm²) acima da PMTA do fabricante (' + fab.toFixed(2).replace('.', ',') + ' kgf/cm²)'}
                  description="O laudo vai adotar uma pressão maior que a da placa. Se for intencional, registre a justificativa nas observações; se não, confira o valor." />;
              }}</Form.Item>
              <Row gutter={16} align="middle">
                <Col span={5}>
                  <Form.Item name="pmta_final_manual" label="PMTA Final" style={{ marginBottom: 0 }}>
                    <InputNumber style={{ width: '100%' }} precision={2} decimalSeparator="," placeholder="Informe a PMTA final" />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item name="pmta_final_manual_unidade" label="Unidade" initialValue="kgf" style={{ marginBottom: 0 }}>
                    <Select>
                      <Option value="kgf">kgf/cm²</Option>
                      <Option value="psi">psi</Option>
                      <Option value="bar">bar</Option>
                      <Option value="mpa">MPa</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={16} style={{ paddingTop: 22 }}>
                  <Space>
                    <Button onClick={() => {
                      const pmtaFinal = form.getFieldValue('pmta_final_manual');
                      const unid = form.getFieldValue('pmta_final_manual_unidade') || 'kgf';
                      if (!pmtaFinal) { message.warning('Informe a PMTA Final primeiro!'); return; }
                      const conv = { kgf: 1, psi: 0.070307, bar: 1.01972, mpa: 10.1972 };
                      const pmtaKgf = pmtaFinal * (conv[unid] || 1);
                      const th = pmtaKgf * 1.3;
                      const thPsi = th / 0.070307;
                      const thBar = th / 1.01972;
                      const thMpa = th / 10.1972;
                      setThCalculado(`${th.toFixed(2).replace('.',',')} kgf/cm² | ${thPsi.toFixed(2).replace('.',',')} psi | ${thBar.toFixed(2).replace('.',',')} bar | ${thMpa.toFixed(3).replace('.',',')} MPa`);
                    }}>Calcular TH</Button>
                    <Button icon={<CalculatorOutlined />} onClick={async () => {
                      const pmtaFinal = form.getFieldValue('pmta_final_manual');
                      const unid = form.getFieldValue('pmta_final_manual_unidade') || 'kgf';
                      const fluido = form.getFieldValue('fluido') !== 'outro' ? form.getFieldValue('fluido') : form.getFieldValue('fluido_outro');
                      const volume = form.getFieldValue('volume');
                      if (!pmtaFinal) { message.warning('Informe a PMTA Final primeiro!'); return; }
                      try {
                        const r = await api.post('/laudos/classificar', { pmta_final_manual: pmtaFinal, pmta_final_manual_unidade: unid, fluido, volume, volume_unidade: form.getFieldValue('volume_unidade') || 'L' });
                        form.setFieldsValue({ classe: r.data.classe_fluido, grupo: String(r.data.grupo) });
                        message.success('Grupo ' + r.data.grupo + ' | Classe ' + r.data.classe_fluido + ' — Revise se necessário.');
                      } catch { message.error('Erro ao classificar'); }
                    }}>Sugerir Classificação</Button>
                    {thCalculado && <Text style={{ color: '#1a6ca8', fontWeight: 'bold' }}>TH = {thCalculado} (consultivo)</Text>}
                  </Space>
                </Col>
              </Row>
            </div>

            {/* Classificação do Equipamento */}
            <div style={{ marginTop: 12, padding: 12, background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Classificação do Equipamento (NR-13):</Text>
              <Row gutter={16} align="middle">
                <Col span={4}><Form.Item name="classe" label="Classe do Fluido"><Input placeholder="A, B, C ou D" /></Form.Item></Col>
                <Col span={4}><Form.Item name="grupo" label="Grupo"><Input placeholder="1 a 5" /></Form.Item></Col>
                <Col span={4} style={{ paddingTop: 22 }}>
                  <Button icon={<CalculatorOutlined />} onClick={async () => {
                    const grupo = form.getFieldValue('grupo');
                    const classe = form.getFieldValue('classe');
                    if (!grupo || !classe) { message.warning('Preencha Classe e Grupo primeiro'); return; }
                    try {
                      const r = await api.post('/laudos/estimar-categoria', { grupo, classe });
                      form.setFieldsValue({ categoria: r.data.categoria });
                      message.success('Categoria estimada: ' + r.data.categoria);
                    } catch(e) { message.error('Erro: ' + (e.response?.data?.erro || e.message)); }
                  }}>Estimar Categoria</Button>
                </Col>
                <Col span={4}><Form.Item name="categoria" label="Categoria"><Input placeholder="I a V" /></Form.Item></Col>
              </Row>
              <Row gutter={16} align="middle" style={{ marginTop: 8 }}>
                <Col span={24} style={{ paddingTop: 4 }}>
                  <Button type="primary" icon={<CalculatorOutlined />} onClick={async () => {
                    const categoria = form.getFieldValue('categoria');
                    const duas_saidas = form.getFieldValue('duas_saidas');
                    const data_inspecao = form.getFieldValue('data_inspecao');
                    if (!categoria) { message.warning('Preencha a Categoria primeiro!'); return; }
                    if (!data_inspecao) { message.warning('Informe a Data da Inspeção na aba Identificação!'); return; }
                    try {
                      const dataStr = typeof data_inspecao === 'string' ? data_inspecao : data_inspecao.format('YYYY-MM-DD');
                      const grupo = form.getFieldValue('grupo');
                      const classe = form.getFieldValue('classe');
                      const r = await api.post('/laudos/calcular-prazos', { categoria, classe, grupo, duas_saidas, equipamento_movel: form.getFieldValue('equipamento_movel'), data_inspecao: dataStr });
                      form.setFieldsValue({ proxima_inspecao_externa: dayjs(r.data.proxima_inspecao_externa), proxima_inspecao_interna: dayjs(r.data.proxima_inspecao_interna) });
                      message.success('Prazos: Externa ' + r.data.prazo_externo_anos + ' anos (' + r.data.proxima_inspecao_externa + ') | Interna ' + r.data.prazo_interno_anos + ' anos (' + r.data.proxima_inspecao_interna + ')');
                    } catch { message.error('Erro ao calcular prazos'); }
                  }}>Calcular Prazos das Inspeções</Button>
                </Col>
              </Row>
            </div>
          </Card>

          <Card title="Croqui" size="small" style={{ marginTop: 12 }}
            extra={<Button size="small" onClick={desenharCroqui}>Atualizar</Button>}>
            {croquiSvg
              ? <div style={{ overflowX: 'auto', textAlign: 'center' }}
                  dangerouslySetInnerHTML={{ __html: croquiSvg }} />
              : <span style={{ color: '#8c8c8c' }}>{croquiMsg || 'Preencha as cotas acima.'}</span>}
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8, lineHeight: 1.4 }}>
              Desenhado a partir das cotas desta etapa, pelo mesmo gerador que o PDF usa.
              Cota assumida por falta de medida aparece em laranja no desenho.
            </div>
          </Card>
        </div>

        {/* STEP 3: VIDA REMANESCENTE */}
        <div style={{ display: step === 3 ? 'block' : 'none' }}>
          <VidaRemanescente form={form} laudoId={id ? parseInt(id) : null} dadosRef={dadosRef} />
        </div>

        {/* STEP 4: CHECK LIST */}
        <div style={{ display: step === 4 ? 'block' : 'none' }}>
          <Card title="Check List — C: Conforme | N/C: Não Conforme | N/A: Não Aplicável" size="small">
            {CHECKLIST_ITENS.map(item => (
              <Row key={item.key} gutter={16} align="middle" style={{ marginBottom: 4, padding: '3px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Col span={14}><Text>{item.label}</Text></Col>
                <Col span={10}><CLItem campo={item} /></Col>
              </Row>
            ))}
          </Card>
        </div>
        {/* Campos hidden do checklist para garantir que ficam no form */}
        {step !== 4 && CHECKLIST_ITENS.map(item => (
          <Form.Item key={item.key} name={item.key} hidden><input /></Form.Item>
        ))}

        {/* STEP 5: MANÔMETRO */}
        <div style={{ display: step === 5 ? 'block' : 'none' }}>
          <Card title="Manômetros" size="small" style={{ marginBottom: 16 }}>
            <div style={{ padding: '12px', background: '#f0f4f8', borderRadius: 8, marginBottom: 16 }}>
              <Space>
                <Text strong>O equipamento possui manômetro(s)?</Text>
                <Switch checked={possuiManometro} onChange={setPossuiManometro} checkedChildren="Sim" unCheckedChildren="Não" />
              </Space>
            </div>
            {possuiManometro && <ManometroForm laudoId={id ? parseInt(id) : null} tagVaso={dadosRef.current?.tag || form.getFieldValue('tag') || ''} />}
          </Card>
        </div>

        {/* STEP 6: VÁLVULA DE SEGURANÇA */}
        <div style={{ display: step === 6 ? 'block' : 'none' }}>
          <Card title="Válvulas de Segurança" size="small" style={{ marginBottom: 16 }}>
            <div style={{ padding: '12px', background: '#f0f4f8', borderRadius: 8, marginBottom: 16 }}>
              <Space>
                <Text strong>O equipamento possui válvula(s) de segurança?</Text>
                <Switch checked={possuiValvula} onChange={setPossuiValvula} checkedChildren="Sim" unCheckedChildren="Não" />
              </Space>
            </div>
            {possuiValvula && <ValvulaForm laudoId={id ? parseInt(id) : null} tagVaso={dadosRef.current?.tag || form.getFieldValue('tag') || ''} />}
          </Card>
        </div>

        {/* STEP 7: RESULTADO */}
        <div style={{ display: step === 7 ? 'block' : 'none' }}>
          <Card title="Resultado e Conclusão" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}><Form.Item name="resultado" label="Resultado da Inspeção" rules={[{ required: true }]}><Select><Option value="apto"><Tag color="success">Apto</Tag></Option><Option value="apto_com_observacoes"><Tag color="warning">Apto com Observações</Tag></Option><Option value="reprovado"><Tag color="error">Reprovado</Tag></Option></Select></Form.Item></Col>
              <Col span={8}><Form.Item name="status" label="Status do Laudo" initialValue="em_elaboracao"><Select><Option value="rascunho">Rascunho</Option><Option value="em_elaboracao">Em Elaboração</Option><Option value="concluido">Concluído</Option></Select></Form.Item></Col>
            </Row>
            <Divider orientation="left">Próximas Inspeções</Divider>
            <Row gutter={16}>
              <Col span={8}><Form.Item name="proxima_inspecao_externa" label="Próxima Inspeção Externa"><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
              <Col span={8}><Form.Item name="proxima_inspecao_interna" label="Próxima Inspeção Interna"><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
            </Row>
            <Divider orientation="left">Fotos do Equipamento</Divider>
            <UploadFotos laudoId={id ? parseInt(id) : null} />
            <Divider orientation="left">Croqui do Equipamento</Divider>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Selecione o croqui para usar no prontuário:</Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {Object.values(croquis).map(cq => (
                  <div key={cq.tipo} onClick={() => { setCroquiSelecionado(cq.tipo); form.setFieldsValue({ croqui_tipo: cq.tipo }); dadosRef.current.croqui_tipo = cq.tipo; }}
                    style={{ cursor: 'pointer', border: croquiSelecionado === cq.tipo ? '2px solid #1890ff' : '2px solid #ddd', borderRadius: 8, padding: 8, textAlign: 'center', width: 120, background: croquiSelecionado === cq.tipo ? '#e6f7ff' : '#fff' }}>
                    <img src={'/api/upload/padrao/' + cq.tipo + '/preview'} alt={cq.label} style={{ width: 80, height: 80, objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />
                    <div style={{ fontSize: 11, marginTop: 4 }}>{cq.label}</div>
                  </div>
                ))}
                {Object.keys(croquis).length === 0 && <Text type="secondary">Nenhum croqui cadastrado. Adicione em Configurações → Croquis e Tabelas.</Text>}
              </div>
              <Form.Item name="croqui_tipo" hidden><input /></Form.Item>
            </div>
            <Form.Item name="observacoes" style={{ marginTop: 16 }} label={<span>Recomendações e Observações{' '}
              <Button size="small" type="link" onClick={async () => {
                try {
                  form.setFieldsValue({ observacoes_manual: false }); dadosRef.current.observacoes_manual = false;
                  const r = await api.post('/laudos/calcular', { ...dadosRef.current, ...form.getFieldsValue(true) });
                  const texto = Array.isArray(r.data.recomendacoes) ? r.data.recomendacoes.join('\n') : (r.data.recomendacoes || '');
                  const m = mesclarObs(form.getFieldValue('observacoes') || '', texto);
                  form.setFieldsValue({ observacoes: m }); dadosRef.current.observacoes = m;
                  message.success('Recomendações automáticas refeitas');
                } catch (e) { message.error('Erro ao refazer as recomendações'); }
              }}>refazer automáticas</Button></span>}>
              <Input.TextArea rows={6} onChange={(e) => { form.setFieldsValue({ observacoes_manual: true }); dadosRef.current.observacoes_manual = true; dadosRef.current.observacoes = e.target.value; }} />
            </Form.Item>
          </Card>
        </div>

        {/* STEP 8: REVISÃO */}
        {step === 8 && (
          <div>
            <Alert message="Revise todos os dados antes de salvar o laudo." type="info" showIcon style={{ marginBottom: 16 }} />
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card title="Identificação" size="small">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Tipo de Laudo">{form.getFieldValue('tipo_laudo') || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Número OS">{form.getFieldValue('numero_os') || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Data Inspeção">{fmtData(form.getFieldValue('data_inspecao'))}</Descriptions.Item>
                    <Descriptions.Item label="Técnico">{form.getFieldValue('tecnico') || '-'}</Descriptions.Item>
                    {artFilial && <Descriptions.Item label="ART do Cliente"><Tag color="green">{artFilial}</Tag></Descriptions.Item>}
                  </Descriptions>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Equipamento" size="small">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="TAG">{form.getFieldValue('tag') || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Tipo">{form.getFieldValue('tipo_equipamento') || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Fabricante">{form.getFieldValue('fabricante') || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Modelo/Posição">{form.getFieldValue('posicao') || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Nº Série">{form.getFieldValue('numero_serie') || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Ano Fabricação">{form.getFieldValue('ano_fabricacao') || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Fluido">{({'ar':'Ar','agua':'Água','oleo':'Óleo','ar-oleo':'Ar-Óleo','gas':'Gás','areia':'Areia','vapor':'Vapor','nitrogenio':'Nitrogênio','oxigenio':'Oxigênio','argonio':'Argônio','outro':'Outro'})[form.getFieldValue('fluido')] || form.getFieldValue('fluido_outro') || form.getFieldValue('fluido') || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Volume">{fmt(form.getFieldValue('volume'))} {form.getFieldValue('volume_unidade')}</Descriptions.Item>
                    <Descriptions.Item label="Material">{({'aco':'Aço','ferro':'Ferro','cromo':'Cromo','inox':'Inox','outro':'Outro'})[form.getFieldValue('material')] || form.getFieldValue('material_outro') || form.getFieldValue('material') || '-'}</Descriptions.Item>
                    <Descriptions.Item label="ASME VIII, DIV. I">{form.getFieldValue('asm') || '-'}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Pressões" size="small">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="PMO">{fmt(form.getFieldValue('pressao_operacao'))} {form.getFieldValue('pressao_operacao_unidade') || 'kgf/cm²'}</Descriptions.Item>
                    <Descriptions.Item label="PMTA Fabricante">{fmt(form.getFieldValue('pmta_fabricante'))} {({ kgf: 'kgf/cm²', psi: 'psi', bar: 'bar', mpa: 'MPa' })[form.getFieldValue('pmta_fabricante_unidade') || 'kgf']}</Descriptions.Item>
                    <Descriptions.Item label="PTH (registro anterior)">{fmt(form.getFieldValue('th_fabricante'))} kgf/cm²</Descriptions.Item>
                    <Descriptions.Item label="PMTA Final">{fmt(form.getFieldValue('pmta_final_manual'))} {({ kgf: 'kgf/cm²', psi: 'psi', bar: 'bar', mpa: 'MPa' })[form.getFieldValue('pmta_final_manual_unidade') || 'kgf']}
                      <Form.Item noStyle shouldUpdate>{() => {
                const conv = { kgf: 1, psi: 0.070307, bar: 1.01972, mpa: 10.1972 };
                const manual = parseFloat(form.getFieldValue('pmta_final_manual')) * (conv[form.getFieldValue('pmta_final_manual_unidade') || 'kgf'] || 1);
                const fab = parseFloat(form.getFieldValue('pmta_fabricante')) * (conv[form.getFieldValue('pmta_fabricante_unidade') || 'kgf'] || 1);
                if (!(manual > 0 && fab > 0 && manual > fab * 1.0001)) return null;
                return <Alert type="warning" showIcon style={{ marginTop: 6 }}
                  message={'PMTA informada (' + manual.toFixed(2).replace('.', ',') + ' kgf/cm²) acima da PMTA do fabricante (' + fab.toFixed(2).replace('.', ',') + ' kgf/cm²)'}
                  description="O laudo vai adotar uma pressão maior que a da placa. Se for intencional, registre a justificativa nas observações; se não, confira o valor." />;
              }}</Form.Item></Descriptions.Item>
                    <Descriptions.Item label="TH Celum?">{fmtBool(form.getFieldValue('th_celum_aplicado'))}</Descriptions.Item>
                    {form.getFieldValue('data_th') && <Descriptions.Item label="Data TH">{fmtData(form.getFieldValue('data_th'))}</Descriptions.Item>}
                    <Descriptions.Item label="Texto TH">{form.getFieldValue('th_texto_opcao') || '-'}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Dimensional" size="small">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Circunferência">{fmt(form.getFieldValue('medida_circunferencial'))} mm</Descriptions.Item>
                    <Descriptions.Item label="Diâmetro Externo">{fmt(form.getFieldValue('diametro_externo'))} mm</Descriptions.Item>
                    <Descriptions.Item label="Dist. Solda a Solda">{fmt(form.getFieldValue('distancia_solda_solda'))} mm</Descriptions.Item>
                    <Descriptions.Item label="Tensão Admissível">{fmt(form.getFieldValue('tensao'))} kgf/cm²</Descriptions.Item>
                    <Descriptions.Item label="Coef. E (costado / tampo sup. / tampo inf.)">{(() => {
                      const e = (v, pad) => (parseFloat(v) > 0 ? fmt(v, 2) : pad + ' (padrão)');
                      const padTampo = (t) => (String(t || '').toLowerCase().includes('plano') ? '1,00' : '0,70');
                      return e(form.getFieldValue('coef_c'), '0,70') + ' / ' +
                        e(form.getFieldValue('coef_tampo_sup'), padTampo(form.getFieldValue('tipo_tampo_sup'))) + ' / ' +
                        e(form.getFieldValue('coef_tampo_inf'), padTampo(form.getFieldValue('tipo_tampo_inf')));
                    })()}</Descriptions.Item>
                    <Descriptions.Item label="Esp. Casco (mín.)">{fmt(Math.min(...[1,2,3,4,5].map(i => parseFloat(form.getFieldValue('esp_casco_'+i)) || 999).filter(v => v < 999)))} mm</Descriptions.Item>
                    <Descriptions.Item label="Tampo Sup.">{form.getFieldValue('tipo_tampo_sup') || '-'} — {fmt(Math.min(...[1,2,3,4,5].map(i => parseFloat(form.getFieldValue('esp_tampo_'+i)) || 999).filter(v => v < 999)))} mm</Descriptions.Item>
                    <Descriptions.Item label="Tampo Inf.">{form.getFieldValue('tipo_tampo_inf') || '-'} — {fmt(Math.min(...[1,2,3,4,5].map(i => parseFloat(form.getFieldValue('esp_tampo_inf_'+i)) || 999).filter(v => v < 999)))} mm</Descriptions.Item>
                    {calculo && <Descriptions.Item label="PMTA Calculada"><Tag color="blue">{fmt(calculo.pmta_final)} kgf/cm²</Tag></Descriptions.Item>}
                    <Descriptions.Item label="Classificação">{form.getFieldValue('grupo') ? `Grupo ${form.getFieldValue('grupo')} | Classe ${form.getFieldValue('classe') || '-'} | Cat. ${form.getFieldValue('categoria') || '-'}` : '-'}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Instalação e Verificação" size="small">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Tipo Instalação">{form.getFieldValue('instalacao_aberta') ? 'Aberta' : 'Fechada'}</Descriptions.Item>
                    <Descriptions.Item label="Duas saídas">{fmtBool(form.getFieldValue('duas_saidas'))}</Descriptions.Item>
                    <Descriptions.Item label="Local Instalação">{form.getFieldValue('local_instalacao') || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Tipo Inspeção">{form.getFieldValue('tipo_inspecao_periodicidade') || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Modo Inspeção">{form.getFieldValue('tipo_inspecao_modo') || '-'}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Resultado e Prazos" size="small">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Resultado">
                      {form.getFieldValue('resultado') === 'apto' ? <Tag color="success">Apto</Tag> :
                       form.getFieldValue('resultado') === 'apto_com_observacoes' ? <Tag color="warning">Apto com Obs.</Tag> :
                       form.getFieldValue('resultado') === 'reprovado' ? <Tag color="error">Reprovado</Tag> : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Próx. Insp. Externa">{fmtData(form.getFieldValue('proxima_inspecao_externa'))}</Descriptions.Item>
                    <Descriptions.Item label="Próx. Insp. Interna">{fmtData(form.getFieldValue('proxima_inspecao_interna'))}</Descriptions.Item>
                    <Descriptions.Item label="Manômetro">{possuiManometro ? ('Sim' + (dadosRef.current?.manometros?.length ? ' — ' + dadosRef.current.manometros.map(m=>m.tag||'').filter(Boolean).join(', ') : '')) : 'Não'}</Descriptions.Item>
                    <Descriptions.Item label="Válvula Seg.">{possuiValvula ? ('Sim' + (dadosRef.current?.valvulas?.length ? ' — ' + dadosRef.current.valvulas.map(v=>v.tag||'').filter(Boolean).join(', ') : '')) : 'Não'}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              {form.getFieldValue('observacoes') && (
                <Col span={24}>
                  <Card title="Recomendações" size="small">
                    <div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{form.getFieldValue('observacoes')}</div>
                  </Card>
                </Col>
              )}
            </Row>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={async () => {
                try {
                  await salvar('concluido');
                  navigate('/laudos');
                } catch(e) { message.error('Erro: ' + e.message); }
              }} size="large">Salvar e Concluir</Button>
            </div>
          </div>
        )}

      </Form>

      {step < 8 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
          <Button onClick={() => irPara(step - 1)} disabled={step === 0}>← Anterior</Button>
          <Button type="primary" onClick={() => { dadosRef.current.status = 'em_elaboracao'; irPara(step + 1); }}>Salvar e Avançar →</Button>
        </div>
      )}
    </div>
  );
}

export default LaudoForm;
// Mon Sep 14 06:11:31 PM UTC 2026
